import { formatDate } from '@/lib/caseFunctions'
import { Case } from '@/payload-types'
import '@/styles/scrollbar.css'
import Image from 'next/image'
import Link from 'next/link'
import FlowbiteIconComponent from '../flowbite-icon/flowbiteIcon'
import { useScrollButtons } from '@/utils/react-hooks/useScrollButtons'

const cardWidth = 281

const EventsTimeLine = ({ events }: { events: Case[] }) => {
  const { containerRef, canScrollLeft, canScrollRight } = useScrollButtons()
  if (!events || events?.length === 0) {
    return null
  }
  const scrollLeft = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -cardWidth, behavior: 'smooth' })
    }
  }

  const scrollRight = () => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: cardWidth, behavior: 'smooth' })
    }
  }

  return (
    <div className="flex flex-col w-full bg-gray-200 rounded-lg px-2 pt-2 relative">
      <div
        className={`absolute top-1/2 left-0 z-10 transition-opacity duration-300 ease-in-out ${
          canScrollRight ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          onClick={canScrollRight ? scrollRight : undefined}
          className="bg-[#C13D20]/80 hover:bg-[#C13D20] rounded-full p-2 shadow-lg transition-colors"
          aria-label="Scroll left"
        >
          <FlowbiteIconComponent
            iconType="AngleDown"
            className="rotate-90 ml-[0px]"
            iconClassName="w-4 h-4"
          />
        </button>
      </div>

      <div
        className={`absolute top-1/2 right-0 z-10 transition-opacity duration-300 ease-in-out ${
          canScrollLeft ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          onClick={canScrollLeft ? scrollLeft : undefined}
          className="bg-[#C13D20]/80 hover:bg-[#C13D20] rounded-full p-2 shadow-lg transition-colors"
          aria-label="Scroll right"
        >
          <FlowbiteIconComponent
            iconType="AngleDown"
            className="-rotate-90 ml-[0px]"
            iconClassName="w-4 h-4"
          />
        </button>
      </div>

      {/* Outer container with vertical flip */}
      <div className="rotate-180">
        {/* Inner scrollable container with horizontal flip */}
        <div
          ref={containerRef}
          className="scrollable-timeline flex gap-4 overflow-x-auto pt-2 scroll-smooth transition-all"
          style={{
            direction: 'rtl',
          }}
        >
          {events?.map((event) => {
            const coverImageUrl =
              typeof event.coverimage === 'string'
                ? event.coverimage
                : event.coverimage?.sizes?.thumbnail?.url || ''
            const fullImageUrl = `${
              process.env.NEXT_PUBLIC_PAYLOAD_URL ? process.env.NEXT_PUBLIC_PAYLOAD_URL : ''
            }${coverImageUrl}`
            const caseUrl = `/${event.type}/${event.slug}`
            return (
              <Link
                href={caseUrl}
                key={event.id}
                target="_blank"
                rel="noopener noreferrer"
                prefetch={false}
              >
                <div
                  key={event.id}
                  className={`mb-2 rotate-180 bg-gray-50 rounded-lg p-2 flex-none w-[265px] h-[215px] flex flex-col gap-1 drop-shadow-lg`}
                  style={{
                    direction: 'ltr',
                  }}
                >
                  {event.date && (
                    <div className="text-xs text-[#C13D20] border-b border-gray-200 py-1">
                      {formatDate(event.date)}
                    </div>
                  )}
                  <div className="md:text-md text-sm font-bold">{event.title}</div>
                  {event.coverimage && (
                    <div className="flex-1 min-h-0">
                      <Image
                        src={fullImageUrl}
                        alt={event.title || ''}
                        width={300}
                        height={300}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                  )}
                </div>
              </Link>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default EventsTimeLine
